/**
 * Cart expiry countdown.
 *
 * The deadline is keyed to a signature of the cart (item count + total) and
 * held in sessionStorage. Two consequences, both deliberate:
 *
 *   - Navigating between pages keeps one continuous countdown rather than
 *     restarting at 10:00 on every render, which would be visibly fake.
 *   - Changing the cart starts a fresh window, so adding something does not
 *     inherit a clock that has already drained to 00:12.
 *
 * Without the signature the countdown runs out once and then sits at 00:00 for
 * the rest of the session, which reads as broken.
 *
 * Nothing is actually removed when this reaches zero — Shopify carts persist —
 * so on expiry a new window simply begins.
 */
const STORAGE_KEY = 'cart-expiry';

class CartExpiryTimer extends HTMLElement {
  connectedCallback() {
    this.output = this.querySelector('[data-expiry]');
    if (!this.output) return;

    const minutes = Number(this.getAttribute('minutes'));
    // A missing or zero setting would otherwise pin the clock at 00:00.
    this.windowMs = (Number.isFinite(minutes) && minutes > 0 ? minutes : 10) * 60 * 1000;
    this.signature = this.getAttribute('cart-signature') || '';

    this.deadline = this.resolveDeadline();
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  resolveDeadline() {
    const now = Date.now();
    const stored = this.read();

    if (stored && stored.signature === this.signature && stored.deadline > now) {
      return stored.deadline;
    }

    const deadline = now + this.windowMs;
    this.write({ signature: this.signature, deadline });
    return deadline;
  }

  read() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    } catch {
      return null;
    }
  }

  write(value) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      /* Private mode — the deadline lives for this page view only. */
    }
  }

  tick() {
    let remaining = this.deadline - Date.now();

    if (remaining <= 0) {
      this.deadline = this.resolveDeadline();
      remaining = this.deadline - Date.now();
    }

    const total = Math.floor(remaining / 1000);
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;

    this.output.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
}

if (!customElements.get('cart-expiry-timer')) {
  customElements.define('cart-expiry-timer', CartExpiryTimer);
}
